import React, { useCallback, useEffect } from 'react';
import {
	Behaviours,
	Prompts,
	TargetTabs,
	createProvisioningApiExtensions,
} from '@atlassiansox/cross-flow-extensions';
import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import Lozenge from '@atlaskit/lozenge';
import { Grid, Text, Flex, Anchor, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { useIsFullPageIssueView } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	useFireCrossFlowAnalyticsEvents,
	SURFACE_ANALYTICS_PRODUCT_MAP,
} from '@atlassian/surface-analytics';
import { messages } from './messages.tsx';

export const CrossflowIdeaCard = ({
	inwardIssueKey,
	inwardIssueId,
	onDismissAd,
}: {
	inwardIssueKey: string;
	inwardIssueId: string;
	onDismissAd: () => Promise<void>;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isFullPageIssueView: boolean = useIsFullPageIssueView();
	const crossFlow = useCrossFlow();
	const { formatMessage } = useIntl();
	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();

	useEffect(() => {
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'card',
				actionSubjectId: 'crossflowIdeaLinkedCard',
			},
			{
				recommendedProductIds: [SURFACE_ANALYTICS_PRODUCT_MAP.products['jira-product-discovery']],
				source: 'jswIssueViewCard',
			},
		);
	}, [fireViewedUIEvent]);

	const handleOnClick = useCallback(
		async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			if (crossFlow.isEnabled) {
				fireUIAnalytics(createAnalyticsEvent({}), 'card clicked', 'crossflowIdeaLinkedCard', {
					target: isFullPageIssueView ? 'sidebar' : 'newTab',
					inwardIssueKey,
				});

				fireInteractedUIEvent(
					{
						action: 'clicked',
						actionSubject: 'card',
						actionSubjectId: 'crossflowIdeaLinkedCard',
					},
					{
						recommendedProductIds: [
							SURFACE_ANALYTICS_PRODUCT_MAP.products['jira-product-discovery'],
						],
						targetProductId: SURFACE_ANALYTICS_PRODUCT_MAP.products['jira-product-discovery'],
						source: 'jswIssueViewCard',
					},
				);

				// Keep issue view open
				e.preventDefault();

				const extensions = createProvisioningApiExtensions({
					actions: {
						primary: {
							behaviour: Behaviours.LAND,
							prompt: Prompts.RETURN_TO_PRODUCT,
							options: {
								targetTab: TargetTabs.NEW,
								urlParams: { SELECTED_ISSUE_PARAM: inwardIssueKey },
							},
						},
					},
					jobName: 'jira-jpd-issueview-link',
					jobParams: {
						inwardIssueKey,
						inwardIssueId,
					},
				});

				await crossFlow.apiV1.open({
					journey: Journeys.GET_STARTED,
					sourceComponent: 'crossflowIdeaLinkedCard',
					sourceContext: 'crossflowIdeaLinkedCard',
					targetProduct: Targets.JIRA_PRODUCT_DISCOVERY,
					extensions,
					v1: true,
				});
			}
		},
		[
			crossFlow,
			createAnalyticsEvent,
			fireInteractedUIEvent,
			isFullPageIssueView,
			inwardIssueKey,
			inwardIssueId,
		],
	);

	const handleDismissClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.stopPropagation();
		e.preventDefault();
		onDismissAd();
	};

	return (
		<Anchor
			href={`/browse/${inwardIssueKey}`}
			onClick={handleOnClick}
			xcss={ideaCardStyles}
			testId="issue-view-jpd-ideas.ui.crossflow-idea-card.crossflow-linked-idea-card"
		>
			<Grid templateColumns="20px 1fr" gap="space.100">
				<Tooltip content={formatMessage(messages.crossflowIdeaTooltip)}>
					<Flex>
						<LightbulbIcon
							testId="issue-view-jpd-ideas.ui.crossflow-idea-card.crossflow-linked-idea-card-icon"
							color={token('color.text.accent.orange')}
							label={formatMessage(messages.tryLozenge)}
						/>
					</Flex>
				</Tooltip>
				<Flex>
					<Text maxLines={1}>{formatMessage(messages.crossflowIdeaSummary)}</Text>
					<Box xcss={lozengeStyles}>
						<Lozenge appearance="new">{formatMessage(messages.tryLozenge)}</Lozenge>
					</Box>
				</Flex>
			</Grid>
			<Tooltip content={formatMessage(messages.closeIdeasTitle)}>
				<IconButton
					icon={CrossIcon}
					label={formatMessage(messages.ideasDismissLabel)}
					appearance="subtle"
					onClick={handleDismissClick}
					testId="issue-view-jpd-ideas.ui.crossflow-idea-card.issue-view-jpd-ideas-dismiss-idea-card"
				/>
			</Tooltip>
		</Anchor>
	);
};

const ideaCardStyles = xcss({
	textDecoration: 'none',
	background: 'none',
	cursor: 'pointer',

	border: 'solid',
	borderColor: 'color.border',
	borderWidth: '0px',

	borderBottomWidth: 'border.width',
	borderLeftWidth: 'border.width',
	borderRightWidth: 'border.width',

	paddingLeft: 'space.100',
	paddingRight: 'space.100',

	display: 'flex',
	alignItems: 'center',
	height: '40px',
	justifyContent: 'space-between',

	transition: '0.3s',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	':first-of-type': {
		borderTopWidth: 'border.width',
		borderTopLeftRadius: 'radius.small',
		borderTopRightRadius: 'radius.small',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		borderBottomWidth: 'border.width',
		borderBottomLeftRadius: 'radius.small',
		borderBottomRightRadius: 'radius.small',
	},
});

const lozengeStyles = xcss({ marginLeft: 'space.100' });
